/*
 * File-name readings, each for its own consumer: they differ on purpose (which extensions count, whether
 * case and `-` matter), so a change to one must not leak into another. No alias imports: the detection
 * worker bundles this module.
 */

/** The last path segment: a dropped folder hands over relative paths. */
export const baseNameOf = (name: string): string => name.split(/[\\/]/).pop() ?? name

const COMPRESSION = /\.(gz|bz2|zst|xz)$/i
const SAMPLE_FILE_EXTENSION = /\.(fastq|fq|fasta|fa)$/i
const READ_FILE_EXTENSION = /\.(fastq|fq|fasta|fa|fna|txt)$/i

const withoutCompression = (name: string) => name.replace(COMPRESSION, '')

/** For pairing mates: the name without directory, compression and sequence-file extension, untrimmed. */
export const readFileStem = (name: string): string => withoutCompression(baseNameOf(name)).replace(READ_FILE_EXTENSION, '')

/** For the plate and sample-number patterns: the name without directory and a `.fastq`/`.fq` (`.gz`) ending. */
export const fastqBaseName = (name: string): string => baseNameOf(name).replace(/\.(fastq|fq)(\.gz)?$/i, '')

/** For matching a sheet's file column: base name, trimmed, lower case, `-` read as `_`, compression dropped. */
export const comparableFileName = (name: string): string =>
  withoutCompression(baseNameOf(name).trim().toLowerCase().replaceAll('-', '_'))

/** The sample part of a FASTQ file name as written: directory, extensions and the mate or bcl2fastq tail dropped. */
export const sampleStemAsWritten = (name: string): string => {
  const base = withoutCompression(baseNameOf(name).trim()).replace(SAMPLE_FILE_EXTENSION, '')
  const withoutBcl = base.replace(/_S\d+(_L\d{3})?(_[RI][12])?(_\d{3})?$/i, '')
  return withoutBcl !== base ? withoutBcl : base.replace(/[._-](R?[12]|fwd|rev|forward|reverse|ft|rt|F|R)$/i, '')
}

/**
 * The sample part of a FASTQ file name for comparing: the bcl2fastq tail (`_S12_L001_R1_001`) or a
 * trailing mate token (`_R1`, `_1`, `.fwd`, `.ft`, …) dropped, lower case, `-` read as `_` the way the
 * classic import compares sequence ids.
 */
export const sampleStemOf = (name: string): string => sampleStemAsWritten(name).toLowerCase().replaceAll('-', '_')
